import React, {FC, useEffect, useState, useRef} from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from 'components/header/Header.container';
import Footer from 'components/footer/Footer';
import { User } from 'modules/user/types';
import Styles from 'views/chatScreen/ChatScreen.module.scss';
import backToDashboardImage from 'assets/svg/back-to-dashboard.svg';
import ChatSideBar from 'components/chatSideBar/ChatSideBar';
import ChatContent from 'components/chatContent/ChatContent';
import MessageThread from 'components/chatContent/messageThread/MessageThread';
import ChatInfo from 'components/chatContent/chatInfo/ChatInfo';
import { createRoom, useGetMessages, useGetRooms } from 'store/services/firebase-broadcast';
import { ref, get } from 'firebase/database';
import { db, storage, reference, getDownloadURL, listAll } from 'utils/firebase-config';
import { getCurrentDate } from 'helpers/utils';

interface StartScreenProps {
  user: User;
}

const ChatScreen: FC<StartScreenProps> = (props: StartScreenProps) => {
  const [showReplies, setShowReplies] = useState(false);
  const [activateRoom, setActiveRoom] = useState<any>(null);
  const [chatInfo, setChatInfo] = useState(false);
  const [showThreads, setShowThreads] = useState<any>({});
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar toggle
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const location: any = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [selected, setSelected] = useState<any>(location?.state?.selected ?? {});
  const [isJoined, setIsJoined] = useState<boolean>(false);
  // REMOVED: chatContent state - no longer needed, using chat directly from useGetMessages
  const [myRooms, setMyRooms] = useState<any>([]);
  const [key, setKey] = useState(0);
  const [room, setRoom] = useState();
  const { pk }: any = selected ?? {};
  const hasAutoSelectedRef = useRef(false); // Track if we've auto-selected on initial load

  useEffect(() => {
      if (location?.state?.selected) {
        setSelected(location?.state?.selected)
        hasAutoSelectedRef.current = false; // Reset flag when room is selected via navigation
      }
    }, [location?.state?.selected])

  useEffect(() => {
    if (location?.state?.conversations?.length > 0) {
      let roomList = location?.state?.conversations.filter(
        (rrr: any) => rrr?.host?.email === user.email && rrr.is_deleted !== true
      );
      setMyRooms(roomList);
    }
  }, []);

  // Track window size for drawer behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleThreads = (e: any) => {
    setShowThreads(e);
  };

  const { chat, isLoading, hasMore, isLoadingMore, loadOlderMessages, isInitialized } = useGetMessages(key);
  let allRooms = useGetRooms()

  // Auto-select first room when page loads without a selected room
  useEffect(() => {
    // Only auto-select if:
    // 1. We haven't already auto-selected
    // 2. No room is passed via location state
    // 3. No room is currently selected (or selected is empty object)
    // 4. Rooms have been loaded
    const hasNoLocationSelection = !location?.state?.selected || Object.keys(location?.state?.selected || {}).length === 0;
    const hasNoSelection = !selected || Object.keys(selected).length === 0;
    
    if (!hasAutoSelectedRef.current && hasNoLocationSelection && hasNoSelection && allRooms && allRooms.length > 0) {
      // Select the first room from the list
      const firstRoom = allRooms[0];
      
      if (firstRoom && firstRoom.pk) {
        setSelected(firstRoom);
        hasAutoSelectedRef.current = true; // Mark that we've auto-selected
      }
    }
  }, [allRooms, location?.state?.selected, selected]);

  useEffect(() => {
    if (pk !== undefined ) {
      setKey(pk);
    }
  }, [pk]);

  // Only handle thread updates when needed
  useEffect(() => {
    if (chat.length > 0 && showThreads.id) {
      const threadMessage = chat.find((i: any) => i.id === showThreads.id);
      if (threadMessage) {
        setShowThreads(threadMessage);
      }
    }
  }, [chat, showThreads.id]);

  useEffect(() => {
    if (!selected) {
      setIsJoined(false);
      return;
    }

    const joined =
      selected?.host?.pk === user?.pk ||
      (Array.isArray(selected?.participants) &&
        selected.participants.some((p: any) => p?.pk === user?.pk));

    setIsJoined(!!joined);
  }, [selected?.pk, selected?.host?.pk, user?.pk, selected?.participants?.length]);

  useEffect(() => {
    if (selected && Object.keys(selected).length > 0) {
      // Use get instead of onValue to prevent continuous re-renders
      const roomRef = ref(db, `rooms/${selected?.pk}`);
      get(roomRef).then((snapshot) => {
        setRoom(snapshot.val());
      }).catch((error) => {
        console.error("Error loading room:", error);
      });
    }
  }, [selected]);

  useEffect(() => {
    if (room === null) {
      const room = {
        // timeStamp: new Date(),
        roomId: selected?.pk,
        topic: selected.topic,
        host: selected.host,
        created_at: getCurrentDate(),
      };
      createRoom(room).then();
    }
  }, [room]);

  // REMOVED: Unused useEffect that was referencing removed chatContent state

  useEffect(() => {
    if (selected?.pk) {
      // ALWAYS clear attachments when room changes (before checking isJoined)
      // This ensures clean state when switching rooms
      setSelected((prevSelected: any) => ({
        ...prevSelected,
        attachments: []
      }));

      // Only fetch attachments if user is joined
      if (isJoined) {
        // Don't automatically open ChatInfo drawer when selecting a room
        // setChatInfo(true);
        setShowReplies(false);

        const currentRoomId = selected.pk; // capture to avoid stale comparisons
        const listRef = reference(storage, `/${currentRoomId}`);
        
        listAll(listRef)
          .then(async (res) => {
            // Double-check room hasn't changed during async operation
            if (!selected || selected.pk !== currentRoomId) {
              return;
            }

            if (res.items && res.items.length > 0) {
              const urlPromises = res.items.map(itemRef => getDownloadURL(itemRef));
              const urls = await Promise.all(urlPromises);
              
              // Verify room still matches before updating
              setSelected((prevSelected: any) => {
                if (prevSelected?.pk === selected.pk) {
                  return {
                    ...prevSelected,
                    attachments: urls
                  };
                }
                return prevSelected;
              });
            }
          })
          .catch((error) => {
            console.log('Error fetching images:', error);
          });
      }
    }
  }, [selected?.pk, isJoined]); // Include both dependencies

  const callBack =(item: any) => {
    setActiveRoom(item);
    // Close sidebar on mobile when room is selected
    if (window.innerWidth < 400) {
      setSidebarOpen(false);
    }
  }
  
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  }
  
  const handleSidebarClose = () => {
    setSidebarOpen(false);
  }
  
  return (
    <>
      <Header />
      <div className="custom-container">
        <section className={Styles.bodyContent}>
          <div className={`${Styles.startScreenWrapper} ${sidebarOpen ? Styles.sidebarOpen : ''} ${chatInfo ? Styles.chatInfoOpen : ''} ${showReplies && isMobile ? Styles.messageThreadOpen : ''}`}>
            <div className={Styles.sidebarOverlay} onClick={handleSidebarClose} />
            {chatInfo && <div className={Styles.chatInfoOverlay} onClick={() => setChatInfo(false)} />}
            {showReplies && isMobile && <div className={Styles.messageThreadOverlay} onClick={() => setShowReplies(false)} />}
            <ChatSideBar 
              selected={selected} 
              conversations={allRooms} 
              setSelected={setSelected} 
              activateRoom={activateRoom}  
              setActiveRoom={setActiveRoom} 
              callBack={callBack}
              sidebarOpen={sidebarOpen}
              onClose={handleSidebarClose}
              onRoomChange={() => setChatInfo(false)}
            />
            <section className={`${Styles.chatSection} ${!isJoined && Styles.notJoinedWrapper} ${showReplies || chatInfo ? Styles.threadSection : ''} `}>
              <button 
                className={Styles.mobileMenuButton}
                onClick={handleSidebarToggle}
                aria-label="Toggle sidebar"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
              <ChatContent
                key={`chat-${selected?.pk || 'no-room'}`}
                replyItems={(e: any) => handleThreads(e)}
                viewReplies={() => {
                  setShowReplies(true);
                  setChatInfo(false);
                }}
                chatInfo={() => {
                  setChatInfo(!chatInfo);
                  setShowReplies(false);
                }}
                chats={chat}
                selected={selected}
                actions={showReplies}
                activateRoom={activateRoom}
                // ADD: Pagination props for dynamic loading
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                loadOlderMessages={loadOlderMessages}
                isInitialized={isInitialized}
              />
            </section>
            {chatInfo && <ChatInfo info={selected} setSelected={setSelected} allRooms={allRooms} isJoined={isJoined} onClose={() => setChatInfo(false)} isDrawerOpen={chatInfo} />}
            {showReplies && <MessageThread actions={showReplies} selected={selected} item={showThreads} hideReplies={(e: any) => setShowReplies(e)} isDrawerOpen={showReplies && isMobile} />}
          </div>
        </section>
      </div>
      <Footer />
      <div className={Styles.backToDashboard}>
        <Link to="/conversations-nearby">
          <img src={backToDashboardImage} alt="back to dashboard" />
        </Link>
      </div>
    </>
  );
};
export default ChatScreen;
